import { useTranslation } from "react-i18next";
import type {
  InsightsPlatformBreakdown,
  PreflightPlatformCode,
} from "@nexus/contracts";
import { cn } from "@nexus/ui";
import { bandTone, scoreToBand } from "../../lib/score-band";
import { TONE_TEXT } from "../../constants/result-state";
import { AnimatedNumber } from "../AnimatedNumber/AnimatedNumber";
import { PlatformLabel } from "../PlatformLabel/PlatformLabel";

export interface SourcesBreakdownProps {
  platforms: readonly InsightsPlatformBreakdown[];
  /** Filters Past reports to this platform — same drill-through idea as heatmap → market. */
  onSelectPlatform: (platform: PreflightPlatformCode) => void;
  className?: string;
}

/**
 * Hub Sources tab: one row per platform that scored creatives in the current market
 * scope. Clicking a row filters Past reports to that platform.
 */
export function SourcesBreakdown({
  platforms,
  onSelectPlatform,
  className,
}: SourcesBreakdownProps) {
  const { t } = useTranslation();

  return (
    <ul
      className={cn(
        "grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3",
        className,
      )}
      aria-label={t("preflight.sources.listAria", "Sources in this scope")}
    >
      {platforms.map((row) => {
        const avg = row.avgScore;
        const avgTone =
          avg != null ? TONE_TEXT[bandTone(scoreToBand(avg))] : undefined;

        return (
          <li key={row.platform}>
            <button
              type="button"
              onClick={() => onSelectPlatform(row.platform)}
              className={cn(
                "border-border/60 bg-card hover:border-border hover:bg-muted/40",
                "focus-visible:ring-ring flex w-full cursor-pointer flex-col gap-3 rounded-md border p-4 text-left",
                "transition-[color,background-color,border-color] duration-150",
                "focus-visible:ring-2 focus-visible:outline-none",
              )}
              aria-label={t("preflight.sources.filterAria", {
                platform: t(`preflight.platform.${row.platform}`),
                defaultValue: "Filter past reports to {{platform}}",
              })}
            >
              <PlatformLabel
                platform={row.platform}
                className="text-foreground text-sm font-medium"
              />
              <dl className="flex items-end justify-between gap-3">
                <div className="flex min-w-0 flex-col gap-1">
                  <dt className="text-muted-foreground font-mono text-[10px] tracking-widest uppercase">
                    {t("preflight.sources.creatives", "Creatives")}
                  </dt>
                  <dd className="text-foreground text-xl leading-none font-semibold tabular-nums">
                    <AnimatedNumber value={row.creativeCount} />
                  </dd>
                </div>
                <div className="flex min-w-0 flex-col items-end gap-1">
                  <dt className="text-muted-foreground font-mono text-[10px] tracking-widest uppercase">
                    {t("preflight.summary.avgScore")}
                  </dt>
                  <dd
                    className={cn(
                      "text-xl leading-none font-semibold tabular-nums",
                      avgTone ?? "text-muted-foreground",
                    )}
                  >
                    {avg != null ? (
                      <AnimatedNumber value={Math.round(avg)} />
                    ) : (
                      t("preflight.dash")
                    )}
                  </dd>
                </div>
              </dl>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
